import { Link } from "react-router";

import {
  classifyPackFileWriteFailure,
  useCompanionCheckQuery,
  useCompanionFixMutation,
} from "@/api/packFiles";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";

/** Never blocks: shows nothing while loading or when the check fails. */
export function CompanionWarnings({
  projectId,
  fixable,
  fixBlockedReason = null,
  className,
}: {
  projectId: string;
  fixable: boolean;
  fixBlockedReason?: string | null;
  className?: string;
}) {
  const check = useCompanionCheckQuery(projectId);
  const fix = useCompanionFixMutation(projectId);
  const findings = check.data?.findings ?? [];
  if (findings.length === 0) return null;

  const failure = fix.isError ? classifyPackFileWriteFailure(fix.error) : null;
  return (
    <div className={className}>
      <p className="mb-2 text-sm font-semibold">
        Your edit needs a follow-up change
      </p>
      {findings.map((finding, index) => (
        <Notice
          key={`${finding.code}-${index}`}
          className="mb-2"
          action={
            finding.fix === null ? undefined : fixable ? (
              <Button
                type="button"
                size="sm"
                className="bg-orange-600 text-white hover:bg-orange-700"
                disabled={fix.isPending || fixBlockedReason !== null}
                title={fixBlockedReason ?? undefined}
                onClick={() => finding.fix && fix.mutate(finding.fix)}
              >
                Update the schema
              </Button>
            ) : (
              <Link
                className="text-sm underline"
                to={`/projects/${encodeURIComponent(projectId)}/config/files`}
              >
                Open Pack files to fix
              </Link>
            )
          }
        >
          <span
            className="[overflow-wrap:anywhere]"
            title={finding.files.join("\n")}
          >
            {finding.message}
          </span>
        </Notice>
      ))}
      {failure ? (
        <Notice tone="destructive" role="alert">
          {failure.kind === "stale"
            ? "The schema file was changed by someone else. Reload the page and try again."
            : failure.kind === "denied"
              ? "You are not allowed to change this file."
              : failure.message}
        </Notice>
      ) : null}
    </div>
  );
}
